import { useSuspenseQuery } from '@tanstack/react-query'
import { useCompetitionId } from '../../../../common/hooks'
import { getMaxPeriod } from '../../../../widgets/api'
import { ResolvedContext, WidgetContext } from './types'

const FALLBACK_PERIOD = '2025-01-01'

/**
 * Resolves a per-widget context (subject + period) into concrete fetch params.
 * - When use_max_period is true, fetches the latest period with data.
 * - Otherwise uses the explicit period (falling back to a sane constant if empty).
 */
export function useResolvedContext(ctx: WidgetContext): ResolvedContext {
  const competition_id = useCompetitionId()

  const { data: maxPeriodData } = useSuspenseQuery({
    queryKey: [
      'dashboard-resolved-context',
      ctx.period_type,
      ctx.subject_type,
      ctx.subject_id,
      ctx.use_max_period,
      competition_id,
    ],
    queryFn: async () => {
      if (!ctx.use_max_period) return { max_period: null }
      return getMaxPeriod({
        period_type: ctx.period_type,
        subject_type: ctx.subject_type,
        subject_id:
          ctx.subject_id && ctx.subject_id > 0
            ? String(ctx.subject_id)
            : undefined,
        competition_id,
      })
    },
    staleTime: 60 * 1000,
  })

  const period = ctx.use_max_period
    ? maxPeriodData.max_period || ctx.period || FALLBACK_PERIOD
    : ctx.period || FALLBACK_PERIOD

  return {
    subject_type: ctx.subject_type,
    subject_id: ctx.subject_id,
    period_type: ctx.period_type,
    period,
    competition_id,
  }
}
